import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTestimonials, toggleFeaturedTestimonial, toggleActiveTestimonial, deleteTestimonial, ITestimonial } from "../api/testimonials";
import {
	Box,
	Button,
	Typography,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Alert,
	CircularProgress,
	Chip,
	TableContainer,
	Tooltip,
	Fab,
	Snackbar,
	Rating,
	Card,
	CardContent,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';

export default function TestimonialsPage() {
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [deleteTestimonialName, setDeleteTestimonialName] = useState<string>("");
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
		open: false,
		message: "",
		severity: "success"
	});
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Fetch testimonials with React Query
	const { data: testimonialsResponse, isLoading, error } = useQuery({
		queryKey: ["testimonials"],
		queryFn: () => getTestimonials({ limit: 100 }),
	});

	const testimonials = testimonialsResponse?.testimonials || [];

	// Toggle featured mutation
	const toggleFeaturedMutation = useMutation({
		mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
			toggleFeaturedTestimonial(id, featured),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["testimonials"] });
			setSnackbar({
				open: true,
				message: "Featured status updated successfully",
				severity: "success"
			});
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to update featured status",
				severity: "error"
			});
		}
	});

	// Toggle active mutation
	const toggleActiveMutation = useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			toggleActiveTestimonial(id, active),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["testimonials"] });
			setSnackbar({
				open: true,
				message: "Active status updated successfully",
				severity: "success"
			});
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to update active status",
				severity: "error"
			});
		}
	});

	// Delete testimonial mutation
	const deleteMutation = useMutation({
		mutationFn: deleteTestimonial,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["testimonials"] });
			setDeleteId(null);
			setSnackbar({
				open: true,
				message: "Testimonial deleted successfully",
				severity: "success"
			});
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to delete testimonial",
				severity: "error"
			});
		}
	});

	const handleToggleFeatured = (id: string, currentValue: boolean) => {
		toggleFeaturedMutation.mutate({ id, featured: !currentValue });
	};

	const handleToggleActive = (id: string, currentValue: boolean) => {
		toggleActiveMutation.mutate({ id, active: !currentValue });
	};

	const handleDeleteClick = (testimonial: ITestimonial) => {
		setDeleteId(testimonial._id);
		setDeleteTestimonialName(testimonial.clientName.en);
	};

	const handleDeleteConfirm = () => {
		if (deleteId) {
			deleteMutation.mutate(deleteId);
		}
	};

	const handleEdit = (id: string) => {
		navigate(`/dashboard/testimonials/edit/${id}`);
	};

	const handleAdd = () => {
		navigate("/dashboard/testimonials/create");
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box p={3}>
				<Alert severity="error">
					Failed to load testimonials. Please try again later.
				</Alert>
			</Box>
		);
	}

	return (
		<Box p={3}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Typography variant="h4" component="h1">
					Testimonials Management
				</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleAdd}
				>
					Add Testimonial
				</Button>
			</Box>

			{/* Statistics Cards */}
			<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3, flexWrap: 'wrap' }}>
				<Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
					<CardContent>
						<Typography color="textSecondary" gutterBottom>
							Total Testimonials
						</Typography>
						<Typography variant="h4">
							{testimonials.length}
						</Typography>
					</CardContent>
				</Card>
				<Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
					<CardContent>
						<Typography color="textSecondary" gutterBottom>
							Featured
						</Typography>
						<Typography variant="h4">
							{testimonials.filter(t => t.featured).length}
						</Typography>
					</CardContent>
				</Card>
				<Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
					<CardContent>
						<Typography color="textSecondary" gutterBottom>
							Active
						</Typography>
						<Typography variant="h4">
							{testimonials.filter(t => t.active).length}
						</Typography>
					</CardContent>
				</Card>
				<Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
					<CardContent>
						<Typography color="textSecondary" gutterBottom>
							Average Rating
						</Typography>
						<Typography variant="h4">
							{(testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1)}
						</Typography>
					</CardContent>
				</Card>
			</Box>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Client</TableCell>
							<TableCell>Company</TableCell>
							<TableCell>Content</TableCell>
							<TableCell>Rating</TableCell>
							<TableCell>Featured</TableCell>
							<TableCell>Active</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{testimonials.map((testimonial) => (
							<TableRow key={testimonial._id}>
								<TableCell>
									<Typography variant="subtitle2">
										{testimonial.clientName.en}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										{testimonial.clientName.ru}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{testimonial.company.en}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										{testimonial.company.ru}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
										"{testimonial.content.en}"
									</Typography>
									<Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
										"{testimonial.content.ru}"
									</Typography>
								</TableCell>
								<TableCell>
									<Rating value={testimonial.rating} readOnly size="small" />
								</TableCell>
								<TableCell>
									<Switch
										checked={testimonial.featured}
										onChange={() => handleToggleFeatured(testimonial._id, testimonial.featured)}
										color="primary"
									/>
								</TableCell>
								<TableCell>
									<Switch
										checked={testimonial.active}
										onChange={() => handleToggleActive(testimonial._id, testimonial.active)}
										color="primary"
									/>
								</TableCell>
								<TableCell>
									<Box display="flex" gap={1}>
										<Tooltip title="Edit">
											<IconButton
												size="small"
												onClick={() => handleEdit(testimonial._id)}
												color="primary"
											>
												<EditIcon />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete">
											<IconButton
												size="small"
												onClick={() => handleDeleteClick(testimonial)}
												color="error"
											>
												<DeleteIcon />
											</IconButton>
										</Tooltip>
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Delete Confirmation Dialog */}
			<Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
				<DialogTitle>Delete Testimonial</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete the testimonial from "{deleteTestimonialName}"? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteId(null)}>Cancel</Button>
					<Button onClick={handleDeleteConfirm} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
} 